import { Textarea } from "@/components/ui/textarea"
import { EditResponsePairKey } from "@/types/hooks/useTextAreaStates"
import { QuestionnaireResponsePair } from "@/types/questionnaire/models"
import { classNames } from "@/utils/classNames"
import { useLayoutEffect, useRef, useState } from "react"

interface TextAreaListItemProps {
  text: string
  onTextChange: (text: string) => void
  responsePair: QuestionnaireResponsePair
  editResponsePairKey: EditResponsePairKey
  onFocus?: () => void
  elementId?: string
  isResizeLocked?: boolean
}

const MAX_TEXTAREA_HEIGHT = 600 //px
const MIN_TEXTAREA_HEIGHT = 20 //px

export const TextAreaListItem: React.FC<TextAreaListItemProps> = ({
  text,
  onTextChange,
  onFocus,
  elementId,
  isResizeLocked = false,
}) => {
  const textarea = useRef<HTMLTextAreaElement>(null)
  const [textareaHeight, setTextareaHeight] = useState(MIN_TEXTAREA_HEIGHT)

  useLayoutEffect(() => {
    const element = textarea.current
    if (!element) {
      return
    }

    const calcTextAreaHeight = () => {
      // A textarea's scrollHeight can never report less than its own current
      // height (browser spec floor) — measuring without resetting first means
      // any prior wrong-and-too-tall value locks itself in forever, since
      // every later "recompute" just reads that same inflated floor back.
      // Collapse to the content's natural size before measuring.
      const previousHeight = element.style.height
      element.style.height = "auto"
      const textareaStyles = window.getComputedStyle(element)
      const textAreaScrollHeight =
        element.scrollHeight + parseFloat(textareaStyles.paddingTop)
      element.style.height = previousHeight
      return Math.min(
        Math.max(textAreaScrollHeight, MIN_TEXTAREA_HEIGHT),
        MAX_TEXTAREA_HEIGHT,
      )
    }

    setTextareaHeight(calcTextAreaHeight())

    // A one-shot measurement at mount isn't enough: layouts that settle their
    // width asynchronously after the initial paint (e.g. a resizable panel
    // group establishing its panels' widths) can leave this measuring a
    // transitional, too-narrow width — wrapping the text onto extra lines and
    // locking in an inflated height forever. Recompute whenever the textarea's
    // own box actually changes size, so it self-corrects once layout settles.
    const resizeObserver = new ResizeObserver(() => {
      setTextareaHeight(calcTextAreaHeight())
    })
    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <Textarea
      id={elementId}
      ref={textarea}
      name="description"
      value={text}
      onFocus={onFocus}
      onChange={(e) => {
        onTextChange(e.target.value)
      }}
      className={classNames(
        "w-full",
        "px-3.5 py-3",
        "rounded-[10px] border-0 bg-transparent",
        "text-white/70 placeholder:text-white/25",
        "shadow-none focus-visible:outline-hidden focus-visible:ring-0",
      )}
      style={{
        height: textareaHeight,
        resize: isResizeLocked ? "none" : undefined,
      }}
    ></Textarea>
  )
}
